import { useId, useState } from 'react'

import { CheckboxField } from '@krp/ui-kit'

import {
  AQSI_CODE_MAPS,
  AQSI_DELIVERY_FLAGS,
  AQSI_DELIVERY_NUMBERS,
  AQSI_DELIVERY_OPTIONAL_NUMBERS,
  AQSI_SERVICE_GROUP_PREFIX,
  AQSI_POS_QR_LABEL,
  AQSI_POS_NUMBERS,
  AQSI_POS_SKIP_PRINTING_LABEL,
  formatCodeMap,
  objectField,
  parseCodeMap,
  posOf,
  withPosField,
  type AqsiSettingsIssue,
} from '../model/settings'
import '../../../shared/ui/aqsi.css'

export interface AqsiConnectionFormProps {
  settings: Readonly<Record<string, unknown>>
  onChange: (settings: Record<string, unknown>) => void
  disabled: boolean
  issues: readonly AqsiSettingsIssue[]
}

function Issue({ id, issue }: { id: string; issue: AqsiSettingsIssue | undefined }) {
  if (issue === undefined) return null
  return (
    <small id={id} role="alert">
      {issue.message}
    </small>
  )
}

function Text({
  label,
  value,
  onChange,
  disabled,
  issue,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  disabled: boolean
  issue: AqsiSettingsIssue | undefined
}) {
  const id = useId()
  return (
    <div className="krp-field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        value={value}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={issue !== undefined || undefined}
        aria-describedby={issue !== undefined ? `${id}-issue` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      <Issue id={`${id}-issue`} issue={issue} />
    </div>
  )
}

/** Целое поле: пусто — `null` (для необязательных), не число — остаётся строкой и не проходит проверку. */
function NumberInput({
  label,
  value,
  onChange,
  disabled,
  issue,
  min,
  max,
}: {
  label: string
  value: unknown
  onChange: (value: number | string | null) => void
  disabled: boolean
  issue: AqsiSettingsIssue | undefined
  min: number
  max: number
}) {
  const id = useId()
  return (
    <div className="krp-field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        inputMode="numeric"
        value={typeof value === 'number' || typeof value === 'string' ? String(value) : ''}
        disabled={disabled}
        aria-invalid={issue !== undefined || undefined}
        aria-describedby={issue !== undefined ? `${id}-issue` : undefined}
        placeholder={`${min}–${max}`}
        onChange={(event) => {
          const raw = event.target.value.trim()
          onChange(raw === '' ? null : /^-?\d+$/.test(raw) ? Number(raw) : raw)
        }}
      />
      <Issue id={`${id}-issue`} issue={issue} />
    </div>
  )
}

/** Соответствие кодов: строки «ключ=число». Текст живёт в поле, в черновик уходит разобранное. */
function CodeMap({
  label,
  value,
  onChange,
  disabled,
  issue,
}: {
  label: string
  value: unknown
  onChange: (value: Record<string, number> | string) => void
  disabled: boolean
  issue: AqsiSettingsIssue | undefined
}) {
  const id = useId()
  const [text, setText] = useState(() => formatCodeMap(value))
  return (
    <div className="krp-field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        value={text}
        disabled={disabled}
        spellCheck={false}
        placeholder="НДС 20%=1"
        aria-invalid={issue !== undefined || undefined}
        aria-describedby={issue !== undefined ? `${id}-issue` : undefined}
        onChange={(event) => {
          setText(event.target.value)
          onChange(parseCodeMap(event.target.value))
        }}
      />
      <Issue id={`${id}-issue`} issue={issue} />
    </div>
  )
}

/**
 * Настройки кабинета AQSI (форма пакета вида для реестра Platform). Только
 * значение и колбэк: store, запросов и ключа кабинета здесь нет — ключ
 * вводится общим полем секрета формы ресурса и в настройки не попадает.
 */
export function AqsiConnectionForm({ settings, onChange, disabled, issues }: AqsiConnectionFormProps) {
  const issueOf = (field: string) => issues.find((issue) => issue.field === field)
  const legalEntity = objectField(settings, 'legalEntity')
  const delivery = objectField(settings, 'delivery')
  const pos = posOf(settings)
  const groupPrefix = delivery[AQSI_SERVICE_GROUP_PREFIX.key]
  const setDelivery = (key: string, value: unknown) => {
    const next: Record<string, unknown> = { ...delivery, [key]: value }
    if (value === null && (AQSI_DELIVERY_OPTIONAL_NUMBERS.some((item) => item.key === key) || key === AQSI_SERVICE_GROUP_PREFIX.key))
      delete next[key]
    onChange({ ...settings, delivery: next })
  }

  return (
    <div className="aqsi-connection-form">
      <Text
        label="Магазин в кабинете AQSI"
        value={typeof settings.shopId === 'string' ? settings.shopId : ''}
        onChange={(shopId) => onChange({ ...settings, shopId })}
        disabled={disabled}
        issue={issueOf('shopId')}
      />
      <Text
        label="ИНН организации"
        value={typeof legalEntity.registeredInn === 'string' ? legalEntity.registeredInn : ''}
        onChange={(registeredInn) => onChange({ ...settings, legalEntity: { ...legalEntity, registeredInn } })}
        disabled={disabled}
        issue={issueOf('legalEntity.registeredInn')}
      />
      <fieldset>
        <legend>Доставка каталога в кабинет</legend>
        {[...AQSI_DELIVERY_NUMBERS, ...AQSI_DELIVERY_OPTIONAL_NUMBERS].map(({ key, label, min, max }) => (
          <NumberInput
            key={key}
            label={label}
            value={delivery[key]}
            min={min}
            max={max}
            onChange={(value) => setDelivery(key, value)}
            disabled={disabled}
            issue={issueOf(`delivery.${key}`)}
          />
        ))}
        {AQSI_DELIVERY_FLAGS.map(({ key, label }) => (
          <CheckboxField
            key={key}
            label={label}
            checked={delivery[key] === true}
            onChange={(on) => setDelivery(key, on)}
          />
        ))}
        {AQSI_CODE_MAPS.map(({ key, label }) => (
          <CodeMap
            key={key}
            label={label}
            value={delivery[key]}
            onChange={(value) => setDelivery(key, value)}
            disabled={disabled}
            issue={issueOf(`delivery.${key}`)}
          />
        ))}
        <Text
          label={AQSI_SERVICE_GROUP_PREFIX.label}
          value={typeof groupPrefix === 'string' ? groupPrefix : ''}
          onChange={(value) => setDelivery(AQSI_SERVICE_GROUP_PREFIX.key, value === '' ? null : value)}
          disabled={disabled}
          issue={issueOf(`delivery.${AQSI_SERVICE_GROUP_PREFIX.key}`)}
        />
      </fieldset>
      <fieldset>
        <legend>Параметры работы кассы</legend>
        <p className="aqsi-caption">
          Это параметры наших запросов к кассе через кабинет AQSI, а не настройки самой кассы.
          QR-оплата здесь — значение по умолчанию для всех касс источника; у каждой кассы её можно изменить, как и количество банковских слипов:
          РМК → Действия → Настройки РМК.
        </p>
        {AQSI_POS_NUMBERS.map(({ key, label, min, max }) => (
          <NumberInput
            key={key}
            label={label}
            value={pos[key]}
            min={min}
            max={max}
            onChange={(value) => onChange(withPosField(settings, key, value))}
            disabled={disabled}
            issue={issueOf(`pos.${key}`)}
          />
        ))}
        <CheckboxField
          label={AQSI_POS_QR_LABEL}
          checked={pos.paymentMode !== 'card_only'}
          onChange={(on) => onChange(withPosField(settings, 'paymentMode', on ? 'sbp_with_card' : 'card_only'))}
          disabled={disabled}
        />
        <Issue id="aqsi-pos-payment-mode-issue" issue={issueOf('pos.paymentMode')} />
        <CheckboxField
          label={AQSI_POS_SKIP_PRINTING_LABEL}
          checked={pos.skipPrinting === true}
          onChange={(on) => onChange(withPosField(settings, 'skipPrinting', on))}
          disabled={disabled}
        />
        <Issue id="aqsi-pos-skip-printing-issue" issue={issueOf('pos.skipPrinting')} />
      </fieldset>
    </div>
  )
}
